// entitlementSlice — Redux slice for account entitlement and feature gating.

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AccountEntitlement, EntitlementTier } from "../../types/index.ts";
import type { RootState } from "../index.ts";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface EntitlementState {
  entitlement: AccountEntitlement | null;
}

const FREE_DEFAULTS: Omit<AccountEntitlement, "tenantId"> = {
  tier: "free",
  maxContexts: 1,
  maxScenarios: 1,
  overlaysEnabled: false,
  schemaNavigationEnabled: false,
};

const PAID_DEFAULTS: Omit<AccountEntitlement, "tenantId"> = {
  tier: "paid",
  maxContexts: Infinity,
  maxScenarios: Infinity,
  overlaysEnabled: true,
  schemaNavigationEnabled: true,
};

const initialState: EntitlementState = {
  entitlement: null,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const entitlementSlice = createSlice({
  name: "entitlement",
  initialState,
  reducers: {
    setTier(
      state,
      action: PayloadAction<{ tenantId: string; tier: EntitlementTier }>,
    ) {
      const { tenantId, tier } = action.payload;
      const defaults = tier === "paid" ? PAID_DEFAULTS : FREE_DEFAULTS;
      state.entitlement = { tenantId, ...defaults };
    },

    setEntitlement(state, action: PayloadAction<AccountEntitlement>) {
      state.entitlement = action.payload;
    },
  },
});

export const { setTier, setEntitlement } = entitlementSlice.actions;
export default entitlementSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectEntitlement = (state: RootState) => state.entitlement.entitlement;
export const selectTier = (state: RootState) => state.entitlement.entitlement?.tier ?? "free";
export const selectMaxContexts = (state: RootState) =>
  state.entitlement.entitlement?.maxContexts ?? 1;
export const selectMaxScenarios = (state: RootState) =>
  state.entitlement.entitlement?.maxScenarios ?? 1;
export const selectOverlaysEnabled = (state: RootState) =>
  state.entitlement.entitlement?.overlaysEnabled ?? false;
export const selectSchemaNavigationEnabled = (state: RootState) =>
  state.entitlement.entitlement?.schemaNavigationEnabled ?? false;

// ---------------------------------------------------------------------------
// Feature gate checks
// ---------------------------------------------------------------------------

export function checkFeatureGate(
  entitlement: AccountEntitlement | null,
  feature: "overlays" | "schemaNavigation" | "multiContext" | "multiScenario",
): boolean {
  if (!entitlement) return false;
  switch (feature) {
    case "overlays":
      return entitlement.overlaysEnabled;
    case "schemaNavigation":
      return entitlement.schemaNavigationEnabled;
    case "multiContext":
      return entitlement.maxContexts > 1;
    case "multiScenario":
      return entitlement.maxScenarios > 1;
    default:
      return false;
  }
}
